
























































































































































import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import { ProgramChain } from "@/data/models/ProgramChain";
import { IresDevice } from "@/data/models/IresDevice";
import {
  TrendEntry,
  TrendResponse,
  ConfidenceLevel,
  getQualityScoreColor,
  filterTrendEntriesToModelVersion,
  resolveLatestModelVersion,
} from "@/data/models/AiPrediction";
import { AiPredictionApi, AiPredictionApiError } from "@/api/AiPredictionApi";
import { formatDate } from "@/utils/DateFormatter";
import { formatFloat } from "@/utils/LocalisationHelper";
import { getAiPredictionsAccess } from "@/data/repository/ClientRepository";
import { getModelConfidenceStyle, getTrackColor } from "@/utils/timelineTrackStyle";
import { dayLabelStep, LabelRowDef, visibleLabelGroups } from "@/utils/RenoTimeSeriesLabels";
import {
  AxisInterval,
  clampToWindow,
  dailyEntryInterval,
  dayInAxisUnits,
  isDailyEntryVisible,
} from "@/utils/RenoTimeSeriesGeometry";

// ─── Layout Constants ────────────────────────────────────────────────────────

const CHART_HEIGHT = 50;
// Room for a three-digit day number at 9 px and a gap.
const DAY_LABEL_MIN_SPACING = 22;
const CHART_PAD = 6;
const DOT_RADIUS = 4;
// Taller than the 11px label, so rows of one group never overlap their neighbours.
const LINE_ROW_HEIGHT = 12;
// Quality score buckets:
// - bad: 0.00 - 0.35
// - review: 0.35 - 0.65
// - good: 0.65 - 1.00
const QUALITY_BAD_MAX = 0.35;
const QUALITY_REVIEW_MAX = 0.65;
const QUALITY_EWMA_ALPHA = 0.65;

/** Pre-computed SVG point for quality score chart. */
interface ChartPoint {
  y: number;
  color: string;
}

/**
 * Colored line segment spanning consecutive days with the same confidence.
 * Color carries domain identity; the stroke props carry model confidence.
 */
interface LineSegment {
  start: number;
  end: number;
  stroke: string;
  strokeWidth: number;
  strokeOpacity: number;
  startLevel: ConfidenceLevel;
  endLevel: ConfidenceLevel;
  targetKey: string;
}

@Component
export default class RenoTimeSeries extends Vue {
  @Prop() programChain!: ProgramChain;
  @Prop() iresDevice!: IresDevice;
  @Prop({ default: "" }) modelVersion!: string;
  /**
   * Interactive mode (default) allows day selection, hover tooltips, and the
   * backfill action, and shows the trend for the passed `modelVersion`.
   * When false (read-only Details view), all interactions are disabled and the
   * chart shows only the latest model's trend (hidden if the latest has none).
   */
  @Prop({ default: true }) interactive!: boolean;
  @Prop({ default: () => ({ left: 0, right: 0, width: 0 }) })
  chartArea!: { left: number; right: number; width: number };
  @Prop({ default: null }) visibleRange!: {
    xMin: number; xMax: number;
    startDate: Date;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    timeScale: any;
  } | null;

  // ─── Theme-aware SVG colors ──────────────────────────────────────────
  // The quality zones assume a light backdrop. In dark mode they become
  // low-opacity tints of the quality-score colors (good=green, review=yellow,
  // bad=red — the same family as the score dots and the device-list mini-graph
  // borders), gentle on the #1e1e1e chart panel instead of popping out.

  /** "Good" quality zone fill (top band). */
  get COLOR_ZONE_GOOD(): string {
    return this.$vuetify.theme.dark ? "rgba(76, 175, 80, 0.16)" : "#E8F5E9";
  }

  /** "Review" quality zone fill (middle band). */
  get COLOR_ZONE_REVIEW(): string {
    return this.$vuetify.theme.dark ? "rgba(242, 180, 0, 0.16)" : "#FFF9C4";
  }

  /** "Bad" quality zone fill (bottom band). */
  get COLOR_ZONE_BAD(): string {
    return this.$vuetify.theme.dark ? "rgba(230, 50, 60, 0.16)" : "#FFEBEE";
  }

  /** Threshold dashed-line stroke separating the quality zones. */
  get thresholdStroke(): string {
    return this.$vuetify.theme.dark ? "#757575" : "#BDBDBD";
  }

  /** Connecting trend-line stroke between score dots. */
  get trendStroke(): string {
    return this.$vuetify.theme.dark ? "#BDBDBD" : "#9E9E9E";
  }

  readonly upperThreshold = QUALITY_REVIEW_MAX;
  readonly lowerThreshold = QUALITY_BAD_MAX;
  readonly chartHeight = CHART_HEIGHT;
  readonly dotRadius = DOT_RADIUS;
  readonly lineRowHeight = LINE_ROW_HEIGHT;

  trendData: TrendResponse | null = null;
  isLoadingTrend = false;
  selectedDayIndex = -1;
  /** Incremented per `loadTrend` call; only the newest request may write its response. */
  private trendRequestSeq = 0;

  // Backfill state
  isBackfilling = false;
  backfillJobId = "";
  backfillProgress = 0;
  backfillTotalDays = 0;
  backfillDaysProcessed = 0;
  private backfillPollTimer: number | null = null;

  // ─── Computed ──────────────────────────────────────────────────────

  get chainId(): string {
    return this.programChain?.id ?? "";
  }

  get serialNo(): string {
    return this.iresDevice?.serialNo ?? "";
  }

  get isPredictionFeatureActivated(): boolean {
    return getAiPredictionsAccess() ?? false;
  }

  get showTimeSeries(): boolean {
    return !!this.chainId && !!this.serialNo && this.trendEntries.length > 0;
  }

  /**
   * Draws only with a usable chart area. While the window switches between phone and desktop sizes
   * the graph can report a half-laid-out area, and every SVG coordinate came out NaN (hundreds of
   * console errors per switch).
   */
  get isChartAreaReady(): boolean {
    const area = this.chartArea;

    return (
      !!area &&
      area.width > 0 &&
      Number.isFinite(area.left) &&
      Number.isFinite(area.right) &&
      area.right > area.left
    );
  }

  get trendEntries(): TrendEntry[] {
    if (!this.trendData?.trend) return [];

    // Both views show exactly one model version, hiding entirely when it has no
    // trend data. "Latest" comes from the backend field, falling back to the
    // newest entry's model so the view still works if the backend predates it.
    //
    // Read-only (Details) view: always the latest.
    // Quality view: the selected version, filtered here even though the fetch
    // already filtered server-side, because `trendData` outlives a switch into
    // this view: until the refetch lands it still holds the Details fetch with
    // every version, and drawing that unfiltered showed older models' days for a
    // moment. Until AiPredictions has resolved a version, show the latest (its
    // own default), so switching in from Details keeps the same strip.
    const raw = this.trendData.trend;
    const latest = resolveLatestModelVersion(raw, this.trendData.latestAvailableModel);
    const shownVersion = this.interactive
      ? (this.modelVersion?.trim() || latest)
      : latest;
    const entries = filterTrendEntriesToModelVersion(raw, shownVersion);

    // Sort chronologically, then deduplicate by date (keep latest per date)
    const sorted = [...entries].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
        || new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
    const byDate = new Map<string, TrendEntry>();
    for (const entry of sorted) {
      // Normalize date to YYYY-MM-DD to deduplicate
      const dateKey = entry.date.substring(0, 10);
      byDate.set(dateKey, entry); // Later entries overwrite earlier ones
    }
    return Array.from(byDate.values());
  }

  /** One day in the graph's current x unit: minutes, hours or n-day steps. */
  private get axisDayLength(): number {
    return this.visibleRange ? dayInAxisUnits(this.visibleRange.timeScale) : 1;
  }

  /** The x range a daily entry scores: the day ENDING at its date. */
  private entryInterval(entry: TrendEntry): AxisInterval {
    return dailyEntryInterval(this.entryXValue(entry), this.axisDayLength);
  }

  /**
   * Entries with something to say about the visible x range (all of them when there is no range yet).
   *
   * An **overlap** test against each entry's scored day, not a containment test on its date. A daily
   * entry is anchored at midnight while the axis unit below two days is minutes or hours, so a drying
   * from 08:50 to 10:44 (x 530 to 644 in minutes) has its only score at x 1440: the old ±1 axis unit
   * margin, commented as "roughly 1 day" and actually one minute here, dropped it. The strip stayed
   * visible, because that is gated on `trendEntries`, and drew nothing.
   */
  get visibleEntries(): TrendEntry[] {
    const all = this.trendEntries;
    if (!this.visibleRange) return all;
    const { xMin, xMax } = this.visibleRange;

    return all.filter((e) =>
      isDailyEntryVisible(this.entryInterval(e), { xMin, xMax })
    );
  }

  /** Returns the 1-based day number for an entry (its position in the full trend). */
  globalDayNumber(entry: TrendEntry): number {
    return this.trendEntries.indexOf(entry) + 1;
  }

  /** Every day gets a label only while they stay apart; otherwise every 2nd, 5th, 7th … day. */
  get labelledDayStep(): number {
    const days = this.visibleEntries.length;

    return dayLabelStep(days > 0 ? this.stripPixelWidth / days : 0, DAY_LABEL_MIN_SPACING);
  }

  isDayLabelled(entry: TrendEntry): boolean {
    return this.globalDayNumber(entry) % this.labelledDayStep === 0;
  }

  /** The label rows to render: the targets this drying's trend carries. */
  get labelGroups(): LabelRowDef[][] {
    return visibleLabelGroups(this.trendEntries);
  }



  /** CSS for the data strip, aligned with graph x-axis via margins. */
  get stripStyle(): Record<string, string> {
    const rightMargin = this.chartArea.width - this.chartArea.right;
    return {
      "margin-left": `${this.chartArea.left}px`,
      "margin-right": `${rightMargin}px`,
    };
  }

  /** Pre-computed chart dot y-positions and colors. */
  get chartPoints(): ChartPoint[] {
    return this.visibleEntries.map((_, i) => {
      const smoothedScore = this.smoothedQualityScores[i];
      return {
        y: this.scoreToY(smoothedScore),
        color: this.scoreColor(smoothedScore),
      };
    });
  }

  /**
   * EWMA-smoothed quality scores for a less jagged trend line.
   *
   * `alpha` controls responsiveness:
   * - higher alpha -> follows recent changes more closely
   * - lower alpha -> smoother but less reactive
   */
  get smoothedQualityScores(): Array<number | undefined> {
    const smoothed: Array<number | undefined> = [];
    let previous: number | null = null;

    for (const entry of this.visibleEntries) {
      const rawScore = entry.qualityScore;
      if (rawScore == null) {
        smoothed.push(undefined);
        continue;
      }

      const clampedScore = Math.max(0, Math.min(1, rawScore));
      if (previous == null) {
        previous = clampedScore;
      } else {
        previous = QUALITY_EWMA_ALPHA * clampedScore + (1 - QUALITY_EWMA_ALPHA) * previous;
      }

      smoothed.push(previous);
    }

    return smoothed;
  }

  /** Pixel width of the data strip area. */
  get stripPixelWidth(): number {
    return this.chartArea.right - this.chartArea.left;
  }

  /** SVG polyline points using pixel coordinates. */
  get chartPolyline(): string {
    const n = this.visibleEntries.length;
    if (n <= 1) return "";
    return this.chartPoints
      .map((pt, i) => `${this.dayPixelX(i)},${pt.y}`)
      .join(" ");
  }

  // ─── Lifecycle ─────────────────────────────────────────────────────

  mounted() {
    this.loadTrend();
  }

  beforeDestroy() {
    this.stopBackfillPolling();
  }

  @Watch("chainId")
  onChainChanged() {
    this.selectedDayIndex = -1;
    // Never draw the previous chain's trend under the new chain's graph while
    // the new one loads (or while the interactive view waits for a version).
    this.trendData = null;
    this.loadTrend();
  }

  @Watch("modelVersion")
  onModelVersionChanged() {
    // The read-only view fetches every version whatever is selected, so a
    // selection change there has nothing new to fetch.
    if (!this.interactive) return;
    this.loadTrend();
  }

  @Watch("interactive")
  onInteractiveChanged() {
    // Switching between Quality (filtered by selected version) and Details
    // (all versions, filtered to latest) changes what we must fetch.
    this.selectedDayIndex = -1;
    this.loadTrend();
  }

  // ─── Data Loading ──────────────────────────────────────────────────

  /**
   * Fetches the trend for the current chain.
   *
   * Read-only (Details) view fetches all versions and filters to the latest
   * client-side; interactive (Quality) view filters server-side by selection.
   *
   * The interactive view waits until AiPredictions has resolved a version: an
   * unfiltered fetch before that returns every version's days, which were drawn
   * for a moment and then replaced by the selected version's (possibly shorter)
   * trend. Each call also invalidates any request still in flight, so a slower,
   * older response can never overwrite a newer one.
   */
  async loadTrend() {
    const requestSeq = ++this.trendRequestSeq;
    const selectedVersion = this.modelVersion?.trim() ?? "";

    if (!this.chainId || !this.serialNo || (this.interactive && !selectedVersion)) {
      // The invalidated request will not clear the flag itself.
      this.isLoadingTrend = false;
      return;
    }

    this.isLoadingTrend = true;
    try {
      const trendData = await AiPredictionApi.getTrend(
        this.chainId,
        this.serialNo,
        365,
        this.interactive ? selectedVersion : undefined
      );
      if (requestSeq === this.trendRequestSeq) {
        this.trendData = trendData;
      }
    } catch {
      if (requestSeq === this.trendRequestSeq) {
        this.trendData = null;
      }
    } finally {
      if (requestSeq === this.trendRequestSeq) {
        this.isLoadingTrend = false;
      }
    }
  }

  // ─── Position Helpers ──────────────────────────────────────────────

  /**
   * Converts a trend entry's date to the graph's x-axis value using the same
   * coordinate system as DryingProgressGraph (minutes from midnight of startDate,
   * scaled by TimeScale).
   */
  private entryXValue(entry: TrendEntry): number {
    if (!this.visibleRange) return 0;
    const { startDate, timeScale } = this.visibleRange;
    const refMidnight = new Date(startDate);
    refMidnight.setHours(0, 0, 0, 0);
    // Parse entry date as local midnight (matching graph's local-time convention)
    const [y, m, d] = entry.date.substring(0, 10).split("-").map(Number);
    const entryDate = new Date(y, m - 1, d);
    const minutesFromMidnight = (entryDate.getTime() - refMidnight.getTime()) / (1000 * 60);
    return timeScale.scaleMinutes(minutesFromMidnight);
  }

  /** Returns the x position in pixels for visible entry index i. */
  dayPixelX(i: number): number {
    const entries = this.visibleEntries;
    const n = entries.length;
    if (n <= 1) return this.stripPixelWidth / 2;

    if (this.visibleRange) {
      const { xMin, xMax } = this.visibleRange;
      const range = xMax - xMin;
      if (range <= 0) return this.stripPixelWidth / 2;
      // Clamped, because the scored day normally ends after the drying does: the daily run is the next
      // morning. Unclamped, the dot is drawn outside the SVG — in the data, invisible on screen.
      const xVal = clampToWindow(this.entryInterval(entries[i]).end, {
        xMin,
        xMax,
      });
      return ((xVal - xMin) / range) * this.stripPixelWidth;
    }

    // Fallback: evenly spaced by index
    return (i / (n - 1)) * this.stripPixelWidth;
  }

  /** Returns x as a percentage (0–100) for CSS absolute positioning. */
  dayPercent(i: number): number {
    const entries = this.visibleEntries;
    const n = entries.length;
    if (n <= 1) return 50;

    if (this.visibleRange) {
      const { xMin, xMax } = this.visibleRange;
      const range = xMax - xMin;
      if (range <= 0) return 50;
      const xVal = this.entryXValue(entries[i]);
      return ((xVal - xMin) / range) * 100;
    }

    return (i / (n - 1)) * 100;
  }

  // ─── Chart Helpers ─────────────────────────────────────────────────

  scoreToY(score: number | undefined): number {
    if (score == null) return CHART_HEIGHT - CHART_PAD;
    const clamped = Math.max(0, Math.min(1, score));
    return CHART_PAD + (1 - clamped) * (CHART_HEIGHT - 2 * CHART_PAD);
  }

  zoneY(threshold: number): number {
    return CHART_PAD + (1 - threshold) * (CHART_HEIGHT - 2 * CHART_PAD);
  }

  /**
   * Continuous color interpolation shared with device-list quality border.
   */
  scoreColor(score: number | undefined): string {
    return getQualityScoreColor(score);
  }

  qualityTooltip(entry: TrendEntry): string {
    const dateStr = this.formatEntryDate(entry);
    const score = entry.qualityScore != null
      ? formatFloat(entry.qualityScore, 3)
      : "–";
    return `${dateStr}: ${score}`;
  }

  // ─── Label Row Line Segments ───────────────────────────────────────

  /**
   * Generates line segments for label rows.
   *
   * Each prediction entry represents the **previous day's interval**: a
   * confidence level on day `i` is visualised as a line from day `i-1` to day
   * `i`. Color encodes the track's domain (`getTrackColor`), while the
   * confidence level drives line style and opacity (`getModelConfidenceStyle`).
   * Intervals whose confidence is `none` draw no line.
   */
  getSegments(targetKey: string): LineSegment[] {
    const entries = this.visibleEntries;
    if (entries.length < 2) return [];

    const stroke = getTrackColor(targetKey);
    const segments: LineSegment[] = [];

    const pushSegment = (start: number, end: number, level: ConfidenceLevel) => {
      const style = getModelConfidenceStyle(level);
      if (!style) return; // level "none" → no line for this interval
      segments.push({
        start,
        end,
        stroke,
        strokeWidth: style.strokeWidth,
        strokeOpacity: style.strokeOpacity,
        startLevel: level,
        endLevel: level,
        targetKey,
      });
    };

    // First entry: extend to the left edge of the strip (drying start), using
    // start = -1 as a sentinel so segmentXStart() maps it to x = 0.
    pushSegment(-1, 0, this.confidenceLevel(entries[0], targetKey));

    for (let i = 1; i < entries.length; i++) {
      pushSegment(i - 1, i, this.confidenceLevel(entries[i], targetKey));
    }

    return segments;
  }

  /**
   * Returns dots for each day that has a confidence value for the given target.
   * Dots share the track's domain color and inherit the confidence opacity.
   */
  getLabelDots(targetKey: string): { index: number; color: string; opacity: number }[] {
    const entries = this.visibleEntries;
    const color = getTrackColor(targetKey);
    const dots: { index: number; color: string; opacity: number }[] = [];
    for (let i = 0; i < entries.length; i++) {
      const style = getModelConfidenceStyle(this.confidenceLevel(entries[i], targetKey));
      if (style) {
        dots.push({ index: i, color, opacity: style.strokeOpacity });
      }
    }
    return dots;
  }

  /** Segment coordinates between adjacent plotted dots. */
  segmentXStart(seg: LineSegment): number {
    // start = -1 is a sentinel meaning "left edge of strip" (drying start)
    if (seg.start < 0) return 0;
    return this.dayPixelX(seg.start);
  }

  segmentXEnd(seg: LineSegment): number {
    return this.dayPixelX(seg.end);
  }

  private normalizeConfidenceLevel(value: unknown): ConfidenceLevel {
    if (typeof value === "string") {
      const normalized = value.toLowerCase();
      if (
        normalized === "none" ||
        normalized === "low" ||
        normalized === "medium" ||
        normalized === "high"
      ) {
        return normalized as ConfidenceLevel;
      }
    }

    if (value && typeof value === "object" && "confidence" in value) {
      const confidence = (value as { confidence?: unknown }).confidence;
      if (typeof confidence === "string") {
        const normalized = confidence.toLowerCase();
        if (
          normalized === "none" ||
          normalized === "low" ||
          normalized === "medium" ||
          normalized === "high"
        ) {
          return normalized as ConfidenceLevel;
        }
      }
    }

    return "none";
  }

  private confidenceLevel(entry: TrendEntry, targetKey: string): ConfidenceLevel {
    const raw = entry.targetConfidence?.[targetKey] as unknown;
    return this.normalizeConfidenceLevel(raw);
  }

  segmentTooltip(seg: LineSegment): string {
    // seg.start may be -1 (sentinel for left edge); clamp to 0 for entry lookup
    const startEntry = this.visibleEntries[Math.max(0, seg.start)];
    const startDate = this.formatEntryDate(startEntry);
    const endDate = this.formatEntryDate(this.visibleEntries[seg.end]);
    const levelLabel = seg.startLevel === seg.endLevel
      ? seg.startLevel
      : `${seg.startLevel}->${seg.endLevel}`;
    return seg.start === seg.end
      ? `${startDate}: ${levelLabel}`
      : `${startDate}–${endDate}: ${levelLabel}`;
  }

  // ─── Day Selection ────────────────────────────────────────────────

  selectDay(index: number) {
    // Read-only (Details) view: day selection is disabled.
    if (!this.interactive) return;
    // Clamp sentinel value (-1 = left edge / first entry) to 0
    const clampedIndex = Math.max(0, index);
    this.selectedDayIndex = clampedIndex;
    const entry = this.visibleEntries[clampedIndex];
    if (entry) {
      this.$emit("daySelected", {
        date: entry.date,
        dayNumber: this.globalDayNumber(entry),
        entry,
      });
    }
  }

  // ─── Backfill ─────────────────────────────────────────────────────

  async triggerBackfill() {
      if (!this.isPredictionFeatureActivated) {
        this.$emit("backfillError", this.$t("quality.messages.featureNotActivated").toString());
        return;
      }

    if (this.isBackfilling) return;
    this.isBackfilling = true;
    this.backfillProgress = 0;
    this.backfillDaysProcessed = 0;

    try {
      const result = await AiPredictionApi.startBackfill({
        serialNo: this.serialNo,
        chainId: this.chainId,
      });
      this.backfillJobId = result.jobId;
      this.backfillTotalDays = result.totalDays;
      this.startBackfillPolling();
    } catch (error) {
      this.isBackfilling = false;
      if (error instanceof AiPredictionApiError) {
        this.$emit("backfillError", error.apiMessage);
      }
    }
  }

  private startBackfillPolling() {
    this.backfillPollTimer = window.setInterval(async () => {
      try {
        const status = await AiPredictionApi.getBackfillStatus(this.backfillJobId);
        this.backfillDaysProcessed = status.daysProcessed;
        this.backfillTotalDays = status.totalDays;
        this.backfillProgress = status.totalDays > 0
          ? (status.daysProcessed / status.totalDays) * 100
          : 0;

        if (status.status !== "running") {
          this.stopBackfillPolling();
          this.isBackfilling = false;
          await this.loadTrend();
          this.$emit("backfillComplete", status);
        }
      } catch {
        this.stopBackfillPolling();
        this.isBackfilling = false;
      }
    }, 5000);
  }

  private stopBackfillPolling() {
    if (this.backfillPollTimer != null) {
      clearInterval(this.backfillPollTimer);
      this.backfillPollTimer = null;
    }
  }

  // ─── Helpers ──────────────────────────────────────────────────────

  private formatEntryDate(entry: TrendEntry): string {
    const d = new Date(entry.date);
    return isNaN(d.getTime()) ? entry.date : formatDate(d, "dd.MM.");
  }
}
